// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddWishListLineToCart,
    getWishListLinesDataView,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import deleteWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/DeleteWishListLines";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import {
    CartLineCollectionModel,
    CartLineModel,
    ProductDto,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderUpload, { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal, {
    OrderUploadErrorsModalStyles,
} from "@insite/content-library/Components/OrderUploadErrorsModal";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import { Button, Hidden, Select } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps, OverflowMenuProps } from "@insite/mobius/OverflowMenu";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    loadWishListLinesParameter: state.pages.myListDetails.loadWishListLinesParameter,
    selectedWishListLineIds: state.pages.myListDetails.selectedWishListLineIds,
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
    // XNG-Changes:
    productInfosByWishListLineId: state.pages.myListDetails.productInfosByWishListLineId,
    isUpdatingCart: state.context.isUpdatingCart,
});

const mapDispatchToProps = {
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList,
    setEditingSortOrder,
    // XNG-changes
    deleteWishListLines,
    addLinesToCart,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsOptionsStyles {
    wrapper?: InjectableCss;
    leftColumnWrapper?: InjectableCss;
    rightColumnWrapper?: InjectableCss;
    container?: GridContainerProps;
    selectAllGridItem?: GridItemProps;
    selectAll?: CheckboxProps;
    editSortOrder?: CheckboxProps;
    searchGridItem?: GridItemProps;
    addItemsGridItem?: GridItemProps;
    addItemsLink?: LinkPresentationProps;
    addItemsGridContainer?: GridContainerProps;
    productSelectorGridItem?: GridItemProps;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsGridItem?: GridItemProps;
    uploadItemsLink?: LinkPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
    search?: TextFieldProps;
    sortByGridItem?: GridItemProps;
    sortByOverflowMenu?: OverflowMenuProps;
    sortByClickable?: ClickablePresentationProps;
    selectedSortByClickable?: ClickablePresentationProps;
    closeAddItemsToList?: IconProps;
    // XNG-Change
    sortOrderGridItem?: GridItemProps;
    uploadItemsButton?: ButtonPresentationProps;
    closeButton?: ButtonPresentationProps;
    addItemsButton?: ButtonPresentationProps;
    actionButtonsHidden?: HiddenProps;
    actionsWrapper?: InjectableCss;
    overflowMenu?: OverflowMenuPresentationProps;
    selectAllWrapper?: InjectableCss;
    deleteListItemsModal?: TwoButtonModalStyles;
    addSelectedToCartLink?: LinkPresentationProps;
    deleteSelectedLink?: LinkPresentationProps;
    editSortOrderLink?: LinkPresentationProps;
    sortBySelect?: SelectPresentationProps;
    clearIcon?: IconProps;
}

export const myListsDetailsOptionsStyles: MyListsDetailsOptionsStyles = {
    wrapper: {
        css: css`
            @media print {
                display: none;
            }
            display: flex;
            margin: 20px 0;
        `,
    },
    leftColumnWrapper: {
        css: css`
            display: flex;
            width: calc(100% - 40px);
        `,
    },
    rightColumnWrapper: {
        css: css`
            display: flex;
            width: 40px;
            justify-content: center;
            margin-top: 30px;
        `,
    },
    container: {
        gap: 10,
        css: css`
            margin-top: 32px;
        `,
    },
    selectAllGridItem: {
        width: [12, 12, 6, 4, 3],
        css: css`
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: space-between;
                    `,
                    css`
                        justify-content: space-between;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    selectAll: {
        css: css`
            cursor: pointer;
        `,
    },
    editSortOrder: {
        css: css`
            cursor: pointer;
            margin-left: 30px;
        `,
    },
    searchGridItem: { width: [12, 12, 6, 4, 4] },
    addItemsGridItem: {
        width: [12, 12, 6, 4, 5],
        css: css`
            align-items: center;
        `,
    },
    addItemsLink: {
        icon: {
            iconProps: { src: PlusCircle },
        },
        typographyProps: {
            css: css`
                padding-left: 5px;
            `,
        },
    },
    addItemsGridContainer: { gap: 10 },
    productSelectorGridItem: {
        width: [12, 12, 12, 10, 10],
    },
    uploadItemsGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding-top: 30px;
                    `,
                    css`
                        padding-top: 30px;
                    `,
                ])}
        `,
    },
    uploadItemsLink: {
        css: css`
            margin: 15px 0 0;
        `,
    },
    uploadItemsModal: {
        sizeVariant: "large",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 30px;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    search: {
        iconProps: { src: Search },
    },
    sortByOverflowMenu: {
        iconProps: { src: ChevronsUpDown },
    },
    selectedSortByClickable: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    closeAddItemsToList: {
        src: X,
        css: css`
            margin: 8px;
            cursor: pointer;
            align-items: flex-start;
        `,
    },
};

const styles = myListsDetailsOptionsStyles;

const MyListsDetailsOptions = ({
    id,
    wishListSettings,
    wishListDataView,
    wishListLinesDataView,
    loadWishListLinesParameter,
    selectedWishListLineIds,
    editingSortOrder,
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList,
    setEditingSortOrder,
    // XNG-Changes
    productInfosByWishListLineId,
    isUpdatingCart,
    addLinesToCart,
    deleteWishListLines,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [query, setQuery] = useState(loadWishListLinesParameter.query || "");
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const [deleteListItemsModalIsOpen, setDeleteListItemsModalIsOpen] = useState(false);
    const editSortOrderText = editingSortOrder ? translate("Finish Editing Sort Order") : translate("Edit Sort Order");

    useEffect(() => {
        setQuery("");
        setEditingSortOrder({ editingSortOrder: false });
    }, []);

    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!wishListDataView.value) {
                return;
            }

            const searchText = query.trim();
            updateLoadWishListLinesParameter({ query: searchText });
            loadWishListLines();
        }, 200),
        [wishListDataView],
    );

    if (!wishListDataView.value || !wishListSettings) {
        return null;
    }

    const wishList = wishListDataView.value;

    const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value || "";
        setQuery(newQuery);
        debouncedSearch(newQuery);
    };

    const clearButtonHandler = () => {
        setQuery("");
        debouncedSearch("");
    }

    const sortByChangeHandler = (sortBy: string) => {
        setEditingSortOrder({ editingSortOrder: false });
        updateLoadWishListLinesParameter({ sort: sortBy });
        loadWishListLines();
    };

    const isAllSelected =
        wishListLinesDataView.value &&
        wishListLinesDataView.value.length > 0 &&
        wishListLinesDataView.value.every(o => selectedWishListLineIds.indexOf(o.id) >= 0);

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        const allWishListLineIds = value ? wishListLinesDataView?.value?.map(o => o.id) : undefined;
        setAllWishListLinesIsSelected({ isSelected: value, wishListLineIds: allWishListLineIds });
    };

    // XNG-Changes:
    const linesSelected = () => {
        return selectedWishListLineIds.length > 0;
    };

    const enableRemoveSelected: boolean =
        (wishList.allowEdit || !wishList.isSharedList) && linesSelected() && !wishList.isAutogenerated;

    const removeSelectedClickHandler = () => {
        setDeleteListItemsModalIsOpen(true);
    };

    const removeSelectedCancelHandler = () => {
        setDeleteListItemsModalIsOpen(false);
    };

    const onRemoveSelectedSuccess = () => {
        toasterContext.addToast({
            body: translate(`Item${selectedWishListLineIds.length > 1 ? "s" : ""} Deleted`),
            messageType: "success",
        });
        setAllWishListLinesIsSelected({ isSelected: false });
    };

    const removeSelectedSubmitHandler = () => {
        if (!wishListDataView.value || !wishListLinesDataView.value) {
            return;
        }

        setDeleteListItemsModalIsOpen(false);
        const wishListLineIds = wishListLinesDataView.value
            .filter(o => selectedWishListLineIds.indexOf(o.id) > -1)
            .map(o => o.id);
        deleteWishListLines({
            wishListId: wishListDataView.value.id,
            wishListLineIds,
            onSuccess: onRemoveSelectedSuccess,
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    const displayToast = (message: React.ReactNode) => {
        toasterContext.addToast({ body: message, messageType: "success" });
    };

    const onAddToCartSuccess = (apiResult?: CartLineCollectionModel) => {
        displayToast(
            apiResult?.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : siteMessage("Cart_AllProductsAddedToCart"),
        );
    };

    const addToCartClickHandler = (e: any) => {
        e.preventDefault();
        if (!wishList || !wishListLinesDataView.value) {
            return;
        }
        if (linesSelected()) {
            const selectedLines = wishListLinesDataView?.value.filter(o => selectedWishListLineIds.indexOf(o.id) > -1);
            const linesToAdd = selectedLines.filter(
                o =>
                    o.quoteRequired ||
                    o.allowZeroPricing ||
                    productInfosByWishListLineId[o.id]?.pricing?.unitNetPrice !== 0,
            );

            if (linesToAdd.length === 0) {
                toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
                return;
            }

            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: {
                        cartLines: linesToAdd.map(o => o as any as CartLineModel),
                    } as CartLineCollectionModel,
                },
                onSuccess: () => {
                    onAddToCartSuccess({
                        notAllAddedToCart: linesToAdd.length !== selectedLines.length,
                    } as CartLineCollectionModel);
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.();
                    }
                },
            });
        }
    };
    const allQuantitiesAreValid = (wishListLines: WishListLineModel[]): boolean => {
        return wishListLines
            .filter(o => selectedWishListLineIds.length === 0 || selectedWishListLineIds.indexOf(o.id) > -1)
            .every(o => o.qtyOrdered && parseFloat(o.qtyOrdered.toString()) > 0);
    };
    const enableAddToCart = () => {
        const lines = wishListLinesDataView.value;
        return (
            wishList?.canAddToCart &&
            !isUpdatingCart &&
            lines?.some(o => canAddWishListLineToCart(o, productInfosByWishListLineId)) &&
            allQuantitiesAreValid(lines)
        );
    };

    const editingSortOrderChangeHandler = () => {
        editingSortOrder
            ? setEditingSortOrder({ editingSortOrder: false })
            : setEditingSortOrder({ editingSortOrder: true });
    };
    // End of XNG-Changes

    // const editingSortOrderChangeHandler: CheckboxProps["onChange"] = (_, value) => {
    //     setEditingSortOrder({ editingSortOrder: value });
    // };

    const addItemsToListClickHandler = () => {
        setAddItemsToListIsOpen(true);
    };

    const selectProductHandler = async (productInfo: ProductInfo) => {
        await addToWishList({ productInfos: [productInfo], selectedWishList: wishList });
        toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
    };

    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };

    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };

    const uploadProductsHandler = (products: ProductDto[], onSuccess?: () => void) => {
        const productInfos = products.map(o => ({
            productId: o.id,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.selectedUnitOfMeasure ?? "",
        }));
        addToWishList({
            productInfos,
            selectedWishList: wishList,
            onSuccess: () => {
                onSuccess?.();
                setUploadItemsModalIsOpen(false);
                loadWishListIfNeeded({ wishListId: wishList.id });
            },
        });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    // XNG-Changes: disable sort select when list is empty
    const disableActionsUntilLoadList =
        !wishListDataView.value ||
        !wishListLinesDataView.value ||
        wishListLinesDataView.isLoading ||
        !wishListLinesDataView.pagination;

    const isEmptyList = wishListLinesDataView && wishListLinesDataView.value && wishListLinesDataView.value.length <= 0;

    const showRemoveSelected =
        (wishList.allowEdit || !wishList.isSharedList) && linesSelected() && !wishList.isAutogenerated;

    // XNG-Change: changes on the structure - change the sort from button to be select
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.leftColumnWrapper}>
                {!addItemsToListIsOpen && (
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.selectAllGridItem}>
                            <StyledWrapper {...styles.selectAllWrapper}>
                                <Checkbox
                                    {...styles.selectAll}
                                    checked={isAllSelected}
                                    onChange={selectAllChangeHandler}
                                    data-test-selector="selectAllItems"
                                    // XNG-Changes: disable my list details checkboxes when list is empty
                                    disabled={!wishListLinesDataView.value?.length}
                                >
                                    {translate("Select All")}
                                </Checkbox>
                                <Link
                                    disabled={!enableAddToCart() || !linesSelected()}
                                    {...styles.addSelectedToCartLink}
                                    onClick={addToCartClickHandler}
                                    data-test-selector="myListDetails_addListToCart"
                                    aria-label={translate("Add Selected To Cart")}
                                >
                                    {translate("Add Selected To Cart")}
                                </Link>
                                <Link
                                    onClick={removeSelectedClickHandler}
                                    disabled={!enableRemoveSelected}
                                    {...styles.deleteSelectedLink}
                                    aria-label={translate("Delete Selected")}
                                >
                                    {translate("Delete Selected")}
                                </Link>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.searchGridItem}>
                            <TextField
                                {...styles.search}
                                // label={translate("Search")}
                                placeholder={translate("Search products in list")}
                                value={query}
                                onChange={searchChangeHandler}
                                data-test-selector="wishListSearch"
                            />
                            {query && <Icon {...styles.clearIcon} src={X} onClick={clearButtonHandler} />}
                        </GridItem>
                        {!addItemsToListIsOpen && !wishList.isAutogenerated && !wishList.isDynamic && (
                            <GridItem {...styles.sortByGridItem}>
                                <Select
                                    label={translate("Sort By")}
                                    labelPosition="left"
                                    value={loadWishListLinesParameter.sort}
                                    onChange={event => sortByChangeHandler(event.target.value)}
                                    disabled={disableActionsUntilLoadList || isEmptyList}
                                    {...styles.sortBySelect}
                                >
                                    {wishListLinesDataView.value &&
                                        wishListLinesDataView.pagination?.sortOptions?.map(sortOption => (
                                            <option key={sortOption.sortType} value={sortOption.sortType}>
                                                {sortOption.displayName}
                                            </option>
                                        ))}
                                </Select>
                            </GridItem>
                        )}
                        {loadWishListLinesParameter.sort === "SortOrder" && !query && canEditWishList && (
                            <GridItem {...styles.sortOrderGridItem}>
                                <Link
                                    {...styles.editSortOrderLink}
                                    icon={{ iconProps: { src: editingSortOrder ? X : ChevronsUpDown } }}
                                    onClick={editingSortOrderChangeHandler}
                                    data-test-selector="myListDetailPage_editSortOrderLink"
                                    // XNG-Changes: disable mylist details checkboxes when list is empty
                                    disabled={!wishListLinesDataView.value?.length}
                                    aria-label={editSortOrderText}
                                >
                                    {editSortOrderText}
                                </Link>
                            </GridItem>
                        )}
                        {/* <GridItem {...styles.addItemsGridItem}>
                            {canEditWishList && (
                                <Button
                                    {...styles.addItemsButton}
                                    onClick={addItemsToListClickHandler}
                                    data-test-selector="myListDetails_addItemsToList"
                                    aria-label={translate("Add Items to list")}
                                >
                                    {translate("Add Items")}
                                </Button>
                            )}
                        </GridItem> */}
                    </GridContainer>
                )}
                {/* XNG-Change Moved to Actions */}
                {/* {addItemsToListIsOpen && (
                    <>
                        <ProductSelector
                            selectButtonTitle={translate("Add to List")}
                            skipInventoryValidation={true}
                            denyFixedConfiguration={true}
                            onSelectProduct={selectProductHandler}
                            productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                            productIsUnavailableMessage={siteMessage("Product_NotFound")}
                            extendedStyles={styles.productSelectorStyles}
                        />
                        <StyledWrapper {...styles.actionsWrapper}>
                            <Button
                                {...styles.uploadItemsButton}
                                onClick={uploadItemsClickHandler}
                                data-test-selector="myListDetails_uploadItems"
                            >
                                {translate("Upload Items")}
                            </Button>
                            <Modal
                                {...styles.uploadItemsModal}
                                headline={translate("Upload Items To List")}
                                isOpen={uploadItemsModalIsOpen}
                                handleClose={uploadItemsModalCloseHandler}
                                data-test-selector="myListDetails_uploadItemsToListModal"
                            >
                                <GridContainer>
                                    <GridItem width={[12, 12, 6, 6, 6]}>
                                        <OrderUpload
                                            descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                                            checkInventory={false}
                                            templateUrl="/Excel/ListUploadTemplate.xlsx"
                                            onUploadProducts={uploadProductsHandler}
                                            extendedStyles={styles.orderUploadStyles}
                                        />
                                        <OrderUploadErrorsModal
                                            descriptionText={siteMessage("ListUpload_UploadError")}
                                            uploadErrorText={siteMessage("ListUpload_UploadError")}
                                            rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                                            errorReasons={{
                                                [UploadError.ConfigurableProduct]: siteMessage(
                                                    "ListUpload_CannotOrderConfigurable",
                                                ),
                                                [UploadError.StyledProduct]:
                                                    siteMessage("ListUpload_CannotOrderStyled"),
                                                [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                                [UploadError.NotFound]: siteMessage("Product_NotFound"),
                                            }}
                                            extendedStyles={styles.orderUploadErrorsModalStyles}
                                        />
                                    </GridItem>
                                    <GridItem width={[12, 12, 6, 6, 6]}>
                                        <Zone zoneName="UploadInstructions" contentId={id} />
                                    </GridItem>
                                </GridContainer>
                            </Modal>
                            <Button
                                {...styles.closeButton}
                                onClick={() => {
                                    setAddItemsToListIsOpen(false);
                                }}
                            >
                                {translate("Close")}
                            </Button>
                        </StyledWrapper>
                    </>
                )} */}
            </StyledWrapper>
            <TwoButtonModal
                headlineText={translate(`Delete Item${selectedWishListLineIds.length > 1 ? "s" : ""}`)}
                // {...styles.deleteListItemsModal}
                modalIsOpen={deleteListItemsModalIsOpen}
                messageText={translate(
                    `Are you sure you want to delete ${
                        selectedWishListLineIds.length > 1 ? "these items" : "this item"
                    }?`,
                )}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={removeSelectedCancelHandler}
                onSubmit={removeSelectedSubmitHandler}
                submitTestSelector="submitDeleteListItems"
                // XNG-Changes
                extendedStyles={styles.deleteListItemsModal}
            ></TwoButtonModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsOptions),
    definition: {
        group: "My Lists Details",
        displayName: "Options",
        allowedContexts: ["MyListsDetailsPage"],
    },
};

export default widgetModule;
